import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Snackbar,
  Autocomplete,
  Pagination,
} from '@mui/material';
import {
  Add,
  Search,
  Refresh,
  Inventory2,
  FilterList,
  Warehouse,
  Build,
  AccountBalanceWallet,
  Handyman,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  StatusDot,
  TableEmptyState,
} from '../../components/common/pageKit';
import assetService, { Asset, AssetStatus, AssetCategory, ASSET_STATUSES, AssetBlock, AssetSummary } from '../../services/assetService';
import hrService, { EmployeeListItem } from '../../services/hrService';
import { fmtMoney, fmtNum, netBookValue, assigneeName, tintForCategory, ASSET_STATUS_LABEL, ASSET_STATUS_TONE } from './assetFormat';
import AssetFormDialog from './AssetFormDialog';

const PAGE_LIMIT = 20;

const AssetRegisterPage: React.FC = () => {
  const navigate = useNavigate();

  const [summary, setSummary] = useState<AssetSummary | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [blocks, setBlocks] = useState<AssetBlock[]>([]);

  const [statusFilter, setStatusFilter] = useState<AssetStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState<EmployeeListItem | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [newAssetOpen, setNewAssetOpen] = useState(false);

  // Reference data — loaded once.
  useEffect(() => {
    assetService
      .getCategories()
      .then((r) => setCategories(r.categories))
      .catch(() => {
        // Filter/create-dialog dropdowns stay empty; never fabricate options.
      });
    assetService
      .getBlocks()
      .then((r) => setBlocks(r.blocks))
      .catch(() => {});
    hrService
      .getEmployees({ limit: 200 })
      .then((r) => setEmployees(r.employees))
      .catch(() => {});
  }, []);

  const fetchSummary = useCallback(() => {
    assetService
      .getSummary()
      .then(setSummary)
      .catch(() => {
        // KPI row stays hidden — never fabricate these numbers.
      });
  }, []);

  const fetchAssets = useCallback(() => {
    setLoading(true);
    setError(null);
    assetService
      .getAssets({
        status: statusFilter || undefined,
        categoryId: categoryFilter || undefined,
        assignedTo: assigneeFilter?.id,
        search: search || undefined,
        page,
        limit: PAGE_LIMIT,
      })
      .then((r) => {
        setAssets(r.assets);
        setTotalPages(r.pagination.totalPages);
        setTotal(r.pagination.total);
      })
      .catch((err: any) => setError(err.message || 'Failed to load assets'))
      .finally(() => setLoading(false));
  }, [statusFilter, categoryFilter, assigneeFilter, search, page]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Debounce the free-text search so every keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, categoryFilter, assigneeFilter]);

  const clearFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setAssigneeFilter(null);
    setSearchInput('');
    setSearch('');
  };

  const hasFilters = Boolean(statusFilter || categoryFilter || assigneeFilter || search);

  const kpis = useMemo(() => {
    if (!summary) return null;
    return [
      { title: 'Total Assets', value: fmtNum(summary.totalAssets), icon: <Inventory2 />, color: '#0284c7' },
      { title: 'Total Value', value: fmtMoney(summary.totalValue), icon: <AccountBalanceWallet />, color: '#7c3aed' },
      { title: 'In Use', value: fmtNum(summary.inUse), icon: <Warehouse />, color: '#16a34a' },
      { title: 'Under Repair', value: fmtNum(summary.underRepair), icon: <Build />, color: '#d97706' },
      { title: 'Warranty Expiring (30d)', value: fmtNum(summary.warrantyExpiringSoon), icon: <Warehouse />, color: '#0891b2' },
      { title: 'Maintenance Due (7d)', value: fmtNum(summary.maintenanceDue), icon: <Handyman />, color: '#dc2626' },
    ];
  }, [summary]);

  return (
    <Box>
      <PageHeader
        title="Fixed Assets"
        subtitle="Asset register, depreciation and maintenance across the organisation"
        actions={
          <>
            <IconButton onClick={() => { fetchSummary(); fetchAssets(); }} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => setNewAssetOpen(true)}>
              New Asset
            </Button>
          </>
        }
      />

      {kpis && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
            gap: 2,
            mb: 2.5,
          }}
        >
          {kpis.map((k) => (
            <StatCard key={k.title} title={k.title} value={k.value} icon={k.icon} color={k.color} />
          ))}
        </Box>
      )}

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search name, code, serial…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as AssetStatus | '')}
          sx={{ minWidth: 160 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {ASSET_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {ASSET_STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>

        <Autocomplete
          size="small"
          options={employees}
          value={assigneeFilter}
          onChange={(_, v) => setAssigneeFilter(v)}
          getOptionLabel={(o) => [o.first_name, o.last_name].filter(Boolean).join(' ')}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          sx={{ minWidth: 200 }}
          renderInput={(params) => <TextField {...params} label="Assignee" />}
        />

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1080 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Asset</TableCell>
                <TableCell sx={headerCellSx}>Category</TableCell>
                <TableCell sx={headerCellSx} align="right">Gross Block</TableCell>
                <TableCell sx={headerCellSx} align="right">Accum. Depreciation</TableCell>
                <TableCell sx={headerCellSx} align="right">Net Book Value</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Assignee</TableCell>
                <TableCell sx={headerCellSx}>Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : assets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No assets found"
                      hint={hasFilters ? 'Try clearing the filters.' : 'Click "New Asset" to add the first one to the register.'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                assets.map((a) => {
                  const nbv = netBookValue(a.gross_block, a.accumulated_depreciation);
                  const assignee = assigneeName(a.assignee_first_name, a.assignee_last_name);
                  return (
                    <TableRow
                      key={a.id}
                      hover
                      onClick={() => navigate(`/assets/${a.id}`)}
                      sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    >
                      <TableCell sx={{ py: 1.25 }}>
                        <CodeCell code={a.asset_code} tint={tintForCategory(a.category_id, a.category_name)} icon={<Inventory2 />} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, ml: 5.75 }}>
                          {a.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{a.category_name || '—'}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtMoney(a.gross_block)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtMoney(a.accumulated_depreciation)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                        {nbv === null ? '—' : fmtMoney(nbv)}
                      </TableCell>
                      <TableCell>
                        <StatusDot label={ASSET_STATUS_LABEL[a.status]} tone={ASSET_STATUS_TONE[a.status]} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{assignee || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {a.location || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && assets.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {total} asset{total !== 1 ? 's' : ''}
            </Typography>
            {totalPages > 1 && (
              <Pagination size="small" count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
            )}
          </Box>
        )}
      </TableSurface>

      <AssetFormDialog
        open={newAssetOpen}
        mode="create"
        categories={categories}
        blocks={blocks}
        onClose={() => setNewAssetOpen(false)}
        onSaved={() => {
          setToast({ message: 'Asset created', severity: 'success' });
          fetchSummary();
          fetchAssets();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default AssetRegisterPage;
